import { memo, useRef, useEffect, useCallback, useMemo } from 'react';

import {
  MatchReturnDto,
  GameReturnDto,
  EMatchStatus,
  formatMoney,
} from '@skillarea/lib-shared';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import { accountAuthGuard } from './-account-auth-guard';
import styles from './history.module.scss';

import { AccountLayout } from '~/components/Account/AccountLayout';
import {
  matchQueries,
  getItems,
  getLastCursor,
  hasNextPage,
} from '~/stores/matchStore';
import { gameQueries } from '~/stores/gameStore';
import { resolveLocalizedGameContent } from '~/lib/gameLocale';
import { useUserId } from '~/stores/userStore';
import { MatchService } from '~/services/matchService';
import { resolveHistoryPaginationState } from '~/stores/lib/history-pagination';
import { LayoutCard } from '~/components/LayoutCard/LayoutCard';

const LIMIT = 20;
const matchService = new MatchService();

export const Route = createFileRoute('/account/history')({
  component: HistoryPage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

type TMatchResult = 'win' | 'lose' | 'draw';

interface IHistoryTableRowProps {
  match: MatchReturnDto;
  gameName: string;
  result: TMatchResult;
  resultLabel: string;
  formatDate: (date?: Date | string) => string;
}

const HistoryTableRow = memo(function HistoryTableRow({
  formatDate,
  gameName,
  match,
  result,
  resultLabel,
}: IHistoryTableRowProps) {
  return (
    <tr>
      <td>{formatDate(match.createdAt)}</td>
      <td>{gameName || '-'}</td>
      <td>{formatMoney(match.stake, { nullValue: '-' })}</td>
      <td>{formatMoney(match.prize, { nullValue: '-' })}</td>
      <td>
        <span className={cx(styles.result, styles[result])}>{resultLabel}</span>
      </td>
    </tr>
  );
});

function HistoryPage() {
  const queryClient = useQueryClient();
  const userId = useUserId();
  const { i18n, t } = useTranslation('account');
  const isLoadingRef = useRef(false);
  const terminalPageReachedRef = useRef(false);
  const requestedCursorRef = useRef<string | undefined>(undefined);
  const loadMoreTriggerRef = useRef<globalThis.HTMLDivElement | null>(null);

  // Fetch user's matches
  const matchQuery = useSuspenseQuery(
    matchQueries.list({ limit: LIMIT }, queryClient),
  );

  // Fetch all games for mapping
  const gamesQuery = useSuspenseQuery(gameQueries.list({}, queryClient));

  const matches = getItems<string, MatchReturnDto>(matchQuery.data);
  const games = getItems<string | number, GameReturnDto>(gamesQuery.data);

  const tableHeaders = [
    t('history.headers.date'),
    t('history.headers.game'),
    t('history.headers.entryFee'),
    t('history.headers.prize'),
    t('history.headers.result'),
  ] as const;

  const gamesMap = useMemo(() => {
    const map = new Map<string | number, string>();
    games.forEach((game) => {
      map.set(game.id, resolveLocalizedGameContent(game, i18n.language).name);
    });
    return map;
  }, [games, i18n.language]);

  // Helper to get game name by gameId
  const getGameName = useCallback(
    (gameId: number | undefined): string => {
      if (!gameId) return t('history.unknownGame');
      return gamesMap.get(gameId) || t('history.unknownGame');
    },
    [gamesMap, t],
  );

  // Format date as DD.MM.YYYY
  const formatDate = useCallback((date?: Date | string): string => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }, []);

  // Determine if user won the match
  const getResult = useCallback(
    (match: MatchReturnDto): TMatchResult => {
      if (match.winnerId) {
        return match.winnerId === userId ? 'win' : 'lose';
      }

      if (match.status === EMatchStatus.COMPLETED) {
        return 'draw';
      }

      return 'lose';
    },
    [userId],
  );

  const getResultLabel = useCallback(
    (result: TMatchResult): string => t(`history.result.${result}`),
    [t],
  );

  const rows = useMemo(
    () =>
      matches.map((match) => ({
        match,
        gameName: getGameName(match.gameId),
        result: getResult(match),
        resultLabel: getResultLabel(getResult(match)),
      })),
    [matches, getGameName, getResult, getResultLabel],
  );

  // Query key for cache manipulation (must match matchQueries.list)
  const queryKey = useMemo(() => ['match', { limit: LIMIT }] as const, []);

  // Infinite scroll handler - fetch next page and merge with cache
  const loadMore = useCallback(async () => {
    const currentData =
      queryClient.getQueryData<typeof matchQuery.data>(queryKey);
    if (!currentData) return;
    if (isLoadingRef.current) return;
    if (terminalPageReachedRef.current) return;
    if (!hasNextPage(currentData, { limit: LIMIT }, LIMIT)) return;

    const lastCursor = getLastCursor<string, MatchReturnDto>(currentData);
    if (!lastCursor) {
      terminalPageReachedRef.current = true;
      return;
    }

    if (requestedCursorRef.current === lastCursor) {
      return;
    }

    requestedCursorRef.current = lastCursor;

    isLoadingRef.current = true;

    try {
      // Fetch next page directly using cursor
      const response = await matchService.readAll({
        limit: LIMIT,
        cursor: lastCursor,
      });

      // Merge new items with existing cache
      queryClient.setQueryData(queryKey, (oldData: typeof matchQuery.data) => {
        if (!oldData) return oldData;

        const newItems = { ...oldData.items };
        const responseData = Array.isArray(response)
          ? response
          : response.data || [];

        const paginationState = resolveHistoryPaginationState({
          responseData,
          requestCursor: lastCursor,
          limit: LIMIT,
        });

        terminalPageReachedRef.current = paginationState.shouldStop;

        responseData.forEach((item: MatchReturnDto) => {
          newItems[item.id as keyof typeof newItems] = item;
        });

        return {
          ...oldData,
          items: newItems,
          lastCursor: paginationState.nextCursor,
        };
      });
    } catch (error: unknown) {
      requestedCursorRef.current = undefined;
      throw error;
    } finally {
      isLoadingRef.current = false;
    }
  }, [queryClient, queryKey]);

  useEffect(() => {
    terminalPageReachedRef.current = false;
    requestedCursorRef.current = undefined;
  }, []);

  // Setup intersection observer for infinite scroll trigger
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new globalThis.IntersectionObserver(
      (entries) => {
        const hasIntersectingEntry = entries.some(
          (entry) => entry.isIntersecting,
        );
        if (hasIntersectingEntry) {
          void loadMore();
        }
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0,
      },
    );

    observer.observe(trigger);

    return () => {
      observer.disconnect();
    };
  }, [loadMore]);

  useEffect(() => {
    const timeoutId = globalThis.window.setTimeout(() => {
      void loadMore();
    });

    return () => {
      globalThis.window.clearTimeout(timeoutId);
    };
  }, [loadMore]);

  return (
    <AccountLayout>
      <LayoutCard title={t('history.title')}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                {tableHeaders.map((header) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <HistoryTableRow
                  formatDate={formatDate}
                  gameName={row.gameName}
                  key={String(row.match.id)}
                  match={row.match}
                  result={row.result}
                  resultLabel={row.resultLabel}
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className={styles.noData} colSpan={5}>
                    {t('history.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div aria-hidden className={styles.loadMore} ref={loadMoreTriggerRef} />
      </LayoutCard>
    </AccountLayout>
  );
}
